import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { pushToast } from '../Toaster/Toaster.slice'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'
import classnames from 'classnames'
import Datepicker from 'react-tailwindcss-datepicker'
import { useAppDispatch } from '../../shared/redux/hooks'
import { formatUTCDate, formatUTCMonthDay, dateFromUTCDateString, formatDateForApi } from '../../util/string'

export interface InlineEditDatePickerProps {
  name: string
  label: string
  initialValue?: string | null
  minDate?: Date
  className?: string
  hideLabel?: boolean
  disabled?: boolean
  disabledReason?: string
  allowClear?: boolean
  clearLabel?: string
  displayFormat?: 'full' | 'monthDay'
  handleSubmit: (value: string | null) => Promise<void>
  warningCheck?: (value: string | null) => string | null
}

const EDITOR_WIDTH_PX = 280
const CALENDAR_HEIGHT_PX = 340

function pickerValueFrom(value: string | null): { startDate: Date | null; endDate: Date | null } {
  const dateForPicker = dateFromUTCDateString(value ?? undefined) ?? (value ? new Date(value) : null)
  return { startDate: dateForPicker, endDate: dateForPicker }
}

function positionEditor(anchor: DOMRect): { top: number; left: number; popoverDirection: 'up' | 'down' } {
  const spaceBelow = window.innerHeight - anchor.bottom
  const popoverDirection: 'up' | 'down' = spaceBelow < CALENDAR_HEIGHT_PX && anchor.top > spaceBelow ? 'up' : 'down'
  const left = Math.min(Math.max(8, anchor.left), Math.max(8, window.innerWidth - EDITOR_WIDTH_PX - 8))
  const top = popoverDirection === 'down' ? anchor.bottom + 4 : Math.max(8, anchor.top - 4)
  return { top, left, popoverDirection }
}

export const InlineEditDatePicker: React.FC<InlineEditDatePickerProps> = (props) => {
  const {
    label,
    initialValue,
    minDate,
    handleSubmit,
    className,
    hideLabel,
    disabled,
    disabledReason,
    allowClear,
    clearLabel,
    displayFormat = 'full',
    warningCheck,
  } = props
  const dispatch = useAppDispatch()
  const [isEditing, setEditing] = useState(false)
  const [editedValue, setEditedValue] = useState<string | null>(initialValue ?? null)
  const [pendingValue, setPendingValue] = useState(pickerValueFrom(initialValue ?? null))
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showWarning, setShowWarning] = useState<string | null>(null)
  const [editorPosition, setEditorPosition] = useState<{
    top: number
    left: number
    popoverDirection: 'up' | 'down'
  }>({ top: 0, left: 0, popoverDirection: 'down' })
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const anchorRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    setEditedValue(initialValue ?? null)
    setPendingValue(pickerValueFrom(initialValue ?? null))
  }, [initialValue])

  useLayoutEffect(() => {
    if (!isEditing) return
    const update = (): void => {
      const rect = anchorRef.current?.getBoundingClientRect()
      if (rect == null) return
      setEditorPosition(positionEditor(rect))
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [isEditing])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      const target = event.target as Node
      // Portaled editor shell
      if (wrapperRef.current?.contains(target)) {
        return
      }
      // Calendar popup from react-tailwindcss-datepicker (absolute child / legacy class)
      const el = event.target as Element | null
      if (
        el?.closest?.('[data-testid="inline-edit-datepicker-popover"]') != null ||
        el?.closest?.('.ke-inline-datepicker-calendar') != null ||
        el?.closest?.('.react-tailwindcss-datepicker-popover') != null
      ) {
        return
      }
      setEditing(false)
      setError(null)
      setShowWarning(null)
      setPendingValue(pickerValueFrom(editedValue))
    }

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isEditing, editedValue])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isEditing) {
        setEditing(false)
        setError(null)
        setShowWarning(null)
        setPendingValue(pickerValueFrom(editedValue))
      }
    }

    if (isEditing) {
      document.addEventListener('keydown', handleKeyDown)
      return () => {
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [isEditing, editedValue])

  const handleDateChange = (newValue: { startDate: Date | null; endDate: Date | null } | null) => {
    if (newValue) {
      setPendingValue(newValue)
    } else {
      setPendingValue({ startDate: null, endDate: null })
    }
    setError(null)
    setShowWarning(null)
  }

  const closeEditor = (): void => {
    setEditing(false)
    setError(null)
    setShowWarning(null)
    setPendingValue(pickerValueFrom(editedValue))
  }

  const handleSave = async (valueToSave?: string | null) => {
    const newDateStr = valueToSave !== undefined ? valueToSave : formatDateForApi(pendingValue.startDate)

    if (warningCheck && !showWarning) {
      const warning = warningCheck(newDateStr)
      if (warning) {
        setShowWarning(warning)
        return
      }
    }

    setIsSubmitting(true)
    setError(null)

    try {
      await handleSubmit(newDateStr)
      setEditedValue(newDateStr)
      dispatch(pushToast({ message: `${label} updated` }))
      setEditing(false)
      setShowWarning(null)
    } catch (e: any) {
      const errorMessage =
        e.response?.data?.message || e.response?.data?.error || e.message || 'Failed to update'
      setError(errorMessage)
      dispatch(
        pushToast({
          type: 'error',
          message: `Error updating ${label}`,
          description: errorMessage,
        })
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClear = async () => {
    await handleSave(null)
  }

  const handleClick = () => {
    if (disabled) {
      if (disabledReason) {
        dispatch(
          pushToast({
            type: 'error',
            message: 'Cannot edit',
            description: disabledReason,
          })
        )
      }
      return
    }
    setEditing(true)
  }

  const formatDisplayDate = (value: string): string => {
    return displayFormat === 'monthDay' ? formatUTCMonthDay(value) : formatUTCDate(value)
  }

  const editor = isEditing
    ? createPortal(
        <div
          ref={wrapperRef}
          data-testid="inline-edit-datepicker-popover"
          className="fixed z-[9999]"
          style={{
            top: `${editorPosition.top}px`,
            left: `${editorPosition.left}px`,
            transform: editorPosition.popoverDirection === 'up' ? 'translateY(-100%)' : undefined,
          }}
        >
          <div className="rounded-md border border-slate-200 bg-white p-2 shadow-lg dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-center gap-2">
              <div className="w-40">
                <Datepicker
                  useRange={false}
                  asSingle={true}
                  value={pendingValue}
                  onChange={handleDateChange}
                  primaryColor={'indigo'}
                  inputClassName={classnames(
                    'w-full rounded-md border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm',
                    error && 'border-red-500'
                  )}
                  popupClassName={(defaultClassName) =>
                    `${defaultClassName} ke-inline-datepicker-calendar`
                  }
                  popoverDirection={editorPosition.popoverDirection}
                  displayFormat={'MM/DD/YYYY'}
                  minDate={minDate}
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  aria-label="Cancel date edit"
                  onClick={closeEditor}
                  disabled={isSubmitting}
                  className="border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 dark:text-slate-500 active:bg-slate-300 dark:active:bg-slate-600 shadow-md rounded transition-colors disabled:opacity-50"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 stroke-current"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <button
                  type="button"
                  aria-label="Save date"
                  onClick={() => handleSave()}
                  disabled={isSubmitting}
                  className="ml-1.5 border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-indigo-500 dark:text-indigo-600 active:bg-indigo-200 dark:active:bg-indigo-700 shadow-md rounded transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <svg
                      className="animate-spin h-6 w-6 text-indigo-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 stroke-current"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
                {allowClear && editedValue && (
                  <button
                    type="button"
                    onClick={handleClear}
                    disabled={isSubmitting}
                    className="ml-1.5 px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 shadow-md rounded transition-colors disabled:opacity-50"
                  >
                    {clearLabel || 'Clear'}
                  </button>
                )}
              </div>
            </div>

            {error && (
              <div className="flex items-center mt-1 text-xs text-red-600 dark:text-red-400">
                <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                {error}
              </div>
            )}

            {showWarning && (
              <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                <p className="text-xs text-yellow-700 dark:text-yellow-300">{showWarning}</p>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowWarning(null)
                      handleSave()
                    }}
                    disabled={isSubmitting}
                    className="px-2 py-1 text-xs bg-yellow-100 dark:bg-yellow-800 hover:bg-yellow-200 dark:hover:bg-yellow-700 text-yellow-800 dark:text-yellow-200 rounded transition-colors disabled:opacity-50"
                  >
                    Continue anyway
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowWarning(null)}
                    disabled={isSubmitting}
                    className="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 rounded transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )
    : null

  return (
    <div className={classnames(className)}>
      {!(hideLabel ?? false) && (
        <div className="font-medium color-base text-slate-700 dark:text-slate-400 mb-1">{label}</div>
      )}

      <div ref={anchorRef} onClick={isEditing ? undefined : handleClick}>
        <span
          className={classnames(
            'flex items-center px-1 -ml-1 rounded-md transition-colors',
            disabled
              ? 'cursor-not-allowed text-slate-400'
              : isEditing
                ? 'bg-indigo-50 dark:bg-indigo-900'
                : 'hover:bg-indigo-50 dark:hover:bg-indigo-900 cursor-pointer'
          )}
        >
          <span className="mr-1 truncate min-w-0">
            {editedValue ? formatDisplayDate(editedValue) : '-'}
          </span>
          {!disabled && !isEditing && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="shrink-0 h-3.5 w-3.5 text-indigo-500 dark:text-indigo-400 stroke-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </span>
      </div>
      {editor}
    </div>
  )
}

export default InlineEditDatePicker
